import React from 'react';
import { AvCheckbox, AvHighlight, AvTooltip, FieldListObject, FieldTypes, SelectedFieldsType } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { ellipsis, emptyObject, iconSize } from '@fabric-ui/utils';
import { badgeClasses, Box, useTheme } from '@mui/material';
import useStyle from '../../../components/FieldsToolbar/useStyle';
import WithDescriptionTooltip from '../../../components/WithDescriptionTooltip/WithDescriptionTooltip';
import { useAvContext } from '../../../context/AvContextProvider';
import { fieldTypeIconsMap } from '../../ModelManagement/hooks';
import { getDimType, getFieldTitleByExpression } from '../CustomDashboardsUtils/expressionUtils';
import { FieldType } from '../types/types';

type MeasurementsObject = Record<string, { description?: string }>;
interface FieldItemProps {
  field: FieldListObject;
  fieldType: FieldType;
  matchHighlightResults: string[];
  onFieldClick: (field: FieldListObject, fieldType: FieldType) => void;
  page: string;
  selectedFields: SelectedFieldsType;
  measurementsObject?: MeasurementsObject;
  disabled?: boolean;
  disabledTooltip?: string;
  enabledFieldTypesWithOptions?: Set<string | undefined>;
  onApplyExpression?: ({ field, type, selectedValue }: { field: FieldListObject; type: string; selectedValue: any }) => void;
}

const FONT_SIZE = 12;

const getMeasurementDescription = (field: FieldListObject, fieldType: FieldType, measurementsObject: MeasurementsObject = {}) =>
  fieldType === FieldType.Measurements && measurementsObject[field.value]?.description;

const FieldItem: React.FC<FieldItemProps> = ({
  field: originalField,
  fieldType,
  matchHighlightResults,
  onFieldClick,
  page,
  selectedFields,
  measurementsObject = emptyObject,
  disabled = false,
  disabledTooltip = '',
  enabledFieldTypesWithOptions,
  onApplyExpression,
}) => {
  const theme = useTheme();
  const style = useStyle(page);

  const {
    accountEntities: { fieldMap },
  } = useAvContext();

  const field = {
    ...originalField,
    ...(selectedFields[originalField.value]?.expression && { expression: selectedFields[originalField.value].expression }),
  };

  const isMetric = fieldType === FieldType.Measurements;

  const measurementDescription = getMeasurementDescription(field, fieldType, measurementsObject);

  const type = isMetric ? FieldTypes.Measurement : getDimType({ fieldMap, name: field.value });

  const getDisplayText = () => {
    const displayName = field.title || field.value;

    if (selectedFields[field.value]?.expression) {
      return getFieldTitleByExpression({
        expression: selectedFields[field.value].expression!,
        displayName,
      });
    }

    return displayName;
  };

  return (
    <Box
      sx={{
        p: 1,
        ':hover': { backgroundColor: style.fieldHoverBackgroundColor, borderRadius: '2px', cursor: 'pointer' },
      }}
      onClick={disabled ? undefined : () => onFieldClick(field, fieldType)}>
      <WithDescriptionTooltip
        id={field.value}
        title={measurementDescription}
        badgify
        badgeProps={{
          sx: {
            flexGrow: 1,
            width: '100%',
            gap: '2px',
            justifyContent: 'space-between',
            [`.${badgeClasses.badge}`]: {
              width: '13px',
              top: '8px',
              right: '2px',
              position: 'relative',
            },
          },
          ...(type && {
            type,
            selectedFields,
            field,
            onApply: selectedValue => onApplyExpression?.({ field, type, selectedValue }),
          }),
          enabledFieldTypesWithOptions,
        }}
        placement="right">
        {/* font size is also applied to Box since the tooltip enlarges the font to 14 */}
        <Box sx={{ ...flex.row, gap: 1, ...ellipsis, fontSize: FONT_SIZE, span: { width: '100%', ...flex.row, gap: 1 } }}>
          <AvTooltip
            sx={{ fontSize: FONT_SIZE, ...flex.row, gap: 1, width: '100%' }}
            muiProps={{ placement: 'top' }}
            popperProps={{ sx: { '.MuiTooltip-tooltip': { maxWidth: 'none', width: '100%' } } }}
            {...(disabled && { title: disabledTooltip })}>
            <AvCheckbox size="small" indeterminate={false} value={!!selectedFields[field.value]} disabled={disabled} />
            {
              fieldTypeIconsMap(theme.palette, iconSize(16))[
                isMetric ? FieldTypes.Measurement : Array.isArray(field.type) ? JSON.stringify(field.type) : field.type
              ]
            }

            <Box sx={{ ...ellipsis }}>
              <AvHighlight text={getDisplayText()} highlights={matchHighlightResults} />
            </Box>
          </AvTooltip>
        </Box>
      </WithDescriptionTooltip>
    </Box>
  );
};

export default FieldItem;
